import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Spinner } from '../../components/ui/Spinner';
import { failureLabel, STATUS_LABELS } from '../../lib/leadRelay/logView';
import { PROVIDER_CAN_SEND, providerNotWiredMessage, providerVocabulary } from '../../lib/leadRelay/providerVocabulary';
import {
  changedFields,
  fieldsFromForm,
  formFromRoute,
  type RouteFormState,
  saveConsequences,
  shouldPromptTestLead,
} from '../../lib/leadRelay/routeForm';
import { isValidRouteId, newRouteLockProblems, secondActiveRouteProblem, validateLeadRoute } from '../../lib/leadRelay/routeRules';
import type { LeadProvider } from '../../types/leadRelay';
import { listAccounts } from '../accounts';
import { KC } from '../../lib/kyckConnect/links';
import { useCurrentUser } from '../auth';
import {
  createLeadRoute,
  getLeadRelayStatus,
  getLeadRoute,
  listLeadRoutes,
  listModelMapStandards,
  type RelayOutcome,
  sendTestLead,
  updateLeadRoute,
} from './api';
import { type AnytimeFitnessPatch, type CommonPatch, LeadRouteEditorBody, type VirtualYardPatch } from './LeadRouteEditorBody';

// One lead route — create or edit (docs/lead-relay.md §10.1, §25). ADMIN-ONLY.
//
// ⚠️ A ROUTE DECIDES WHICH DEALERSHIP — OR CLUB — RECEIVES A CLIENT'S LEADS. So:
//   - a new route shows ONLY the Setup picker until a setup is chosen; the
//     setup fixes the provider, and the form then shows that provider's fields;
//   - the provider is displayed, never editable (no setter can reach it);
//   - every field that differs from what is saved carries a dot;
//   - a save first states, in words, what is about to change — above all a
//     change to the store / club id, which re-points every future lead;
//   - the form runs the SAME save-time rules as the server (routeRules.ts);
//   - switching a route from inactive to active prompts for a test lead (§8).

export function TestLeadResult({ outcome, provider }: { outcome: RelayOutcome; provider: LeadProvider }) {
  const ok = outcome.status === 'submitted';
  const vocab = providerVocabulary(provider);
  return (
    <div className={`rounded-md border p-3 text-sm ${ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-200 bg-red-50 text-red-800'}`}>
      <p className="font-medium">
        {ok
          ? `${vocab.system} accepted the test lead.`
          : `Not accepted — ${STATUS_LABELS[outcome.status].toLowerCase()}${outcome.failureReason ? `: ${failureLabel(outcome.failureReason, provider)}` : ''}.`}
      </p>
      {ok && (
        <p className="mt-1">
          Accepted is not the same as received: confirm with the {vocab.destination.toLowerCase()} that a "TEST Kyck"
          enquiry arrived at the right place.
        </p>
      )}
      {outcome.lastAttempt?.error && <p className="mt-1">{outcome.lastAttempt.error}</p>}
      {outcome.lastAttempt?.responseBody && (
        <pre className="mt-1 whitespace-pre-wrap break-all text-xs">{outcome.lastAttempt.responseBody}</pre>
      )}
    </div>
  );
}

export function LeadRouteEditPage() {
  const { id: routeParam } = useParams();
  const isNew = routeParam === undefined || routeParam === 'new';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useCurrentUser();
  const isAdmin = user.role === 'admin';

  const detail = useQuery({
    queryKey: ['lead-route', routeParam],
    queryFn: () => getLeadRoute(routeParam!),
    enabled: isAdmin && !isNew,
  });
  const { data: accounts = [] } = useQuery({ queryKey: ['accounts'], queryFn: listAccounts, enabled: isAdmin });
  const { data: status } = useQuery({ queryKey: ['lead-relay-status'], queryFn: getLeadRelayStatus, enabled: isAdmin });
  // Every route, for the one-active-route-per-account warning shown BEFORE save.
  const { data: allRoutes = [] } = useQuery({ queryKey: ['lead-routes'], queryFn: listLeadRoutes, enabled: isAdmin });
  // The shared model maps (§25): a VirtualYard route's rows are a standard plus its overrides.
  const standardsQuery = useQuery({ queryKey: ['lead-model-map-standards'], queryFn: listModelMapStandards, enabled: isAdmin });
  const standards = useMemo(() => standardsQuery.data ?? [], [standardsQuery.data]);
  // Only once loaded — an empty map before then would read as "that standard does not exist".
  const standardsById = useMemo(
    () => (standardsQuery.isSuccess ? new Map(standards.map((s) => [s.id, s.map])) : undefined),
    [standardsQuery.isSuccess, standards],
  );

  const saved = useMemo<RouteFormState | null>(
    () => (detail.data ? formFromRoute(detail.data.route) : null),
    [detail.data],
  );
  // ⛔ null until a NEW route's setup is chosen — the body renders only the
  // picker until then. An existing route is seeded from what is stored.
  const [form, setForm] = useState<RouteFormState | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [promptTest, setPromptTest] = useState(false);
  // Optional: run this test lead through Speed to Lead to an allowlisted tester mobile.
  const [samMobile, setSamMobile] = useState('');
  const [testOutcome, setTestOutcome] = useState<RelayOutcome | null>(null);

  // Re-seed from the server whenever what is stored changes (a save, or an edit
  // elsewhere) — never leave a stale draft over newer stored values.
  useEffect(() => {
    if (saved) setForm(saved);
  }, [saved]);

  // ⛔ No setter can reach the provider: none of these patch types carries it.
  const onCommon = (patch: CommonPatch) => setForm((f) => (f ? { ...f, ...patch } : f));
  const onVirtualYard = (patch: VirtualYardPatch) =>
    setForm((f) => (f && f.provider === 'virtualyard' ? { ...f, ...patch } : f));
  const onAnytimeFitness = (patch: AnytimeFitnessPatch) =>
    setForm((f) => (f && f.provider === 'anytimefitness' ? { ...f, ...patch } : f));

  const changed = useMemo(() => new Set(form ? changedFields(saved, form) : []), [saved, form]);
  const problems = useMemo(
    () =>
      form
        ? [
            ...validateLeadRoute(fieldsFromForm(form), standardsById),
            // A NEW route is created locked; the server refuses it otherwise.
            ...(isNew ? newRouteLockProblems(fieldsFromForm(form)) : []),
          ]
        : [],
    [form, standardsById, isNew],
  );
  const modelMapStandard =
    form?.provider === 'virtualyard' && form.modelMapStandardId
      ? (standards.find((s) => s.id === form.modelMapStandardId) ?? null)
      : null;
  const problemsFor = (field: string) => problems.filter((p) => p.field === field).map((p) => p.message);
  const idProblem =
    isNew && form && !isValidRouteId(form.id) ? 'Route id must be a lowercase slug, e.g. byd-liverpool.' : null;
  const inFlight = detail.data?.inFlightCount ?? 0;
  const consequences = form ? saveConsequences(saved, form, inFlight) : [];
  // ⚠️⚠️ ONE ACTIVE ROUTE PER ACCOUNT — a hard rule. Shown under the Active
  // tick and blocks Save; the server refuses the same save (routeService), so
  // neither path can make it.
  const accountClash = form
    ? secondActiveRouteProblem(isNew ? form.id.trim() : routeParam!, fieldsFromForm(form), allRoutes)
    : null;

  const accountOptions = useMemo(
    () =>
      accounts
        .filter((a) => a.status !== 'cancelled')
        .map((a) => ({ value: a.id, label: a.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [accounts],
  );

  const save = useMutation({
    mutationFn: () => {
      const fields = fieldsFromForm(form!);
      return isNew ? createLeadRoute(form!.id.trim(), fields) : updateLeadRoute(routeParam!, fields);
    },
    onSuccess: (result) => {
      setConfirming(false);
      setWarnings(result.warnings);
      const prompt = form ? shouldPromptTestLead(saved, form) : false;
      void queryClient.invalidateQueries({ queryKey: ['lead-routes'] });
      void queryClient.invalidateQueries({ queryKey: ['lead-route-names'] });
      void queryClient.invalidateQueries({ queryKey: ['lead-route', result.id] });
      toast.success('Route saved.');
      // ONE route (/lead-relay/routes/:id, with `new` as the id) renders both
      // modes, so this is the same component instance: the warnings and the
      // test-lead prompt survive the move to the saved route's URL.
      if (isNew) navigate(`${KC.relayRoutes}/${result.id}`, { replace: true });
      setPromptTest(prompt);
    },
    onError: (err: Error) => {
      setConfirming(false);
      toast.error(err.message);
    },
  });

  const test = useMutation({
    mutationFn: () => sendTestLead(isNew ? form!.id.trim() : routeParam!, samMobile),
    onSuccess: (outcome) => {
      setPromptTest(false);
      setTestOutcome(outcome);
      void queryClient.invalidateQueries({ queryKey: ['lead-log'] });
    },
    onError: (err: Error) => {
      setPromptTest(false);
      toast.error(err.message);
    },
  });

  if (!isAdmin) {
    return (
      <div>
        <PageHeader title="Lead route" backHref={KC.relayRoutes} />
        <p className="text-sm text-muted-foreground">Lead routes are admin-only.</p>
      </div>
    );
  }
  if (!isNew && (detail.isLoading || !saved)) {
    return detail.error ? <p className="text-sm text-red-700">{(detail.error as Error).message}</p> : <Spinner />;
  }

  const canSave = form !== null && problems.length === 0 && !accountClash && !idProblem && (isNew || changed.size > 0);
  // ⛔ A provider the relay cannot send through yet gets no test lead (PROVIDER_CAN_SEND).
  const canSendTest = form !== null && PROVIDER_CAN_SEND[form.provider];
  const system = form ? providerVocabulary(form.provider).system : '';

  return (
    <div>
      <PageHeader
        title={isNew ? 'New lead route' : (saved?.name ?? 'Lead route')}
        description={detail.data?.accountName ?? undefined}
        backHref={KC.relayRoutes}
        actions={
          <div className="flex gap-2">
            {!isNew && (
              <Button
                variant="outline"
                onClick={() => test.mutate()}
                disabled={test.isPending || !status?.relayConfigured || changed.size > 0 || !canSendTest}
                title={form && !canSendTest ? providerNotWiredMessage(form.provider) : undefined}
              >
                {test.isPending && <Spinner className="mr-1 h-4 w-4" />}
                Send test lead
              </Button>
            )}
            {form !== null && (
              <Button onClick={() => setConfirming(true)} disabled={!canSave || save.isPending}>
                {save.isPending && <Spinner className="mr-1 h-4 w-4" />}
                {isNew ? 'Create route' : 'Save changes'}
              </Button>
            )}
          </div>
        }
      />

      {!isNew && (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
          <label htmlFor="sam-test-mobile" className="font-medium">
            Also send to AI SMS: mobile
          </label>
          <input
            id="sam-test-mobile"
            type="tel"
            value={samMobile}
            onChange={(e) => setSamMobile(e.target.value)}
            placeholder="optional"
            className="h-8 w-44 rounded-md border border-input bg-background px-2 text-sm"
          />
          <span className="text-muted-foreground">
            Optional. Must be on the club&apos;s bot allowlist and the club must have AI SMS on. The test lead then carries this
            number and the assistant texts it.
          </span>
        </div>
      )}

      {!isNew && changed.size > 0 && (
        <p className="mb-3 text-sm text-amber-700">Unsaved changes are marked with a dot. Save before sending a test lead.</p>
      )}
      {status && !status.relayConfigured && (
        <p className="mb-3 text-sm text-muted-foreground">
          The relay is not configured on this environment, so test leads cannot be sent.
        </p>
      )}

      {warnings.length > 0 && (
        // ⚠️ Persistent, not a toast: a token mismatch is something to act on.
        <div className="mb-4 flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div className="space-y-1">
            <p className="font-medium">Saved — but check this:</p>
            {warnings.map((w) => (
              <p key={w}>{w}</p>
            ))}
          </div>
        </div>
      )}

      {testOutcome && form && (
        <div className="mb-4">
          <TestLeadResult outcome={testOutcome} provider={form.provider} />
        </div>
      )}

      <LeadRouteEditorBody
        isNew={isNew}
        form={form}
        onChooseSetup={(setup) => setForm(setup.initialForm())}
        changed={changed}
        problemsFor={problemsFor}
        idProblem={idProblem}
        accountOptions={accountOptions}
        accountClash={accountClash}
        inFlight={inFlight}
        onCommon={onCommon}
        onVirtualYard={onVirtualYard}
        onAnytimeFitness={onAnytimeFitness}
        modelMapStandard={modelMapStandard}
      />

      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isNew ? 'Create this route?' : 'Save these changes?'}</DialogTitle>
            <DialogDescription>A route decides where this client's leads are sent.</DialogDescription>
          </DialogHeader>
          <ul className="list-disc space-y-1 pl-5 text-sm">
            {consequences.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending && <Spinner className="mr-1 h-4 w-4" />}
              {isNew ? 'Create route' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={promptTest} onOpenChange={setPromptTest}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send a test lead now?</DialogTitle>
            <DialogDescription>
              This route is now active. A test lead is a real "TEST Kyck" enquiry sent through {system} — the quickest
              proof that the route still works. Then confirm with the recipient that it arrived.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPromptTest(false)}>
              Not now
            </Button>
            <Button onClick={() => test.mutate()} disabled={test.isPending || !status?.relayConfigured || !canSendTest}>
              {test.isPending && <Spinner className="mr-1 h-4 w-4" />}
              Send test lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
